import React, { useRef, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

interface Props {
    value: string[];
    onChange: (emails: string[]) => void;
    label?: string;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (s: string) => EMAIL_RE.test(s.trim());

// Delimiters that end an address, Outlook-style: comma or semicolon.
const DELIMITERS = /[,;]/;

// Pure helpers (exported for unit tests).
// Split typed text into already-complete tokens (everything before the last
// delimiter) and the trailing remainder still being typed.
export const splitOnDelimiter = (raw: string): { complete: string[]; remainder: string } => {
    if (!DELIMITERS.test(raw)) return { complete: [], remainder: raw };
    const parts = raw.split(DELIMITERS);
    const remainder = parts.pop() ?? '';
    return { complete: parts, remainder };
};
// A pasted blob → individual tokens (split on comma/semicolon/whitespace).
export const tokenizePaste = (text: string): string[] => text.split(/[,;\s]+/).filter(Boolean);

/**
 * Outlook-style recipient entry. Type an address and it becomes a chip as
 * soon as you type a comma or semicolon, press Enter or Tab, paste a list,
 * or leave the field. Backspace on an empty input removes the last chip.
 * Recipients are arbitrary external addresses (leadership without POM
 * logins), so this is freeSolo, not a user picker. Invalid entries are NOT
 * silently dropped — they surface an inline message so a fat-fingered
 * address doesn't quietly go unsent.
 */
export const EmailChipsInput: React.FC<Props> = ({
    value, onChange, label = 'Recipients',
    placeholder = 'Type an address; separate with a comma or semicolon', error, helperText,
    disabled,
}) => {
    const [input, setInput] = useState('');
    const [rejected, setRejected] = useState<string[]>([]);
    // Synchronous mirror of `input`. Tab commits then setInput('') batches,
    // so the ensuing blur would re-read stale state and double-commit;
    // reading/clearing the ref makes commit idempotent across Tab→blur.
    const pending = useRef('');

    const setPending = (v: string) => { pending.current = v; setInput(v); };

    // Add the valid, non-duplicate addresses among `raws`; return the invalid
    // ones so the caller can surface them.
    const commit = (raws: string[]): string[] => {
        const bad: string[] = [];
        const additions: string[] = [];
        for (const raw of raws) {
            const email = raw.trim().toLowerCase();
            if (!email) continue;
            if (!isValidEmail(email)) { bad.push(email); continue; }
            if (!value.includes(email) && !additions.includes(email)) additions.push(email);
        }
        if (additions.length) onChange([...value, ...additions]);
        return bad;
    };

    // Commit whatever's pending and clear it synchronously (ref + state), so
    // a following blur has nothing left to commit.
    const commitPending = () => {
        const v = pending.current;
        if (!v.trim()) return;
        setRejected(commit([v]));
        setPending('');
    };

    const handleInput = (raw: string) => {
        if (rejected.length) setRejected([]);
        const { complete, remainder } = splitOnDelimiter(raw);
        if (complete.length) {
            const bad = commit(complete);
            if (bad.length) setRejected(bad);
        }
        setPending(remainder);
    };

    const rejectionMsg = rejected.length
        ? `Not a valid email, not added: ${rejected.join(', ')}`
        : undefined;

    return (
        <Autocomplete
            multiple
            freeSolo
            disabled={disabled}
            options={[]}
            value={value}
            inputValue={input}
            onInputChange={(_, v) => handleInput(v)}
            onChange={(_, next, reason) => {
                if (reason === 'createOption') { commitPending(); return; } // Enter
                // removeOption / clear (chip delete, Backspace-on-empty).
                onChange((next as string[]).filter(isValidEmail));
            }}
            renderTags={(tags, getTagProps) =>
                tags.map((email, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return <Chip key={key} {...chipProps} size="small" label={email} />;
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error || rejected.length > 0}
                    helperText={rejectionMsg || helperText}
                    // Tab commits the current address, then focus moves on
                    // (Enter is handled via createOption).
                    onKeyDown={(e) => { if (e.key === 'Tab') commitPending(); }}
                    onPaste={(e) => {
                        const text = e.clipboardData.getData('text');
                        if (DELIMITERS.test(text) || /\s/.test(text)) {
                            e.preventDefault();
                            setRejected(commit(tokenizePaste(text)));
                            setPending('');
                        }
                    }}
                    // Flush a half-typed address when focus leaves (e.g.
                    // clicking Create). No-op if Tab/Enter already committed.
                    onBlur={commitPending}
                />
            )}
        />
    );
};

export default EmailChipsInput;
